<template>
  <section class="projects-list">
    <div
      v-if="!projectsList"
      class="projects-list__content"
    >
      <pacman-loader />
    </div>
    <div
      v-else-if="projectsList?.length > 0"
      class="projects-list__content"
    >
      <div
        class="projects-list__list"
        :class="`--${viewType}`"
      >
        <gf-project-card
          v-for="project in projectsList"
          :key="project.alias"
          :project="project"
          :show-you-owner-label="showYouOwnerLabel"
          @star="starProject"
          @leave="onLeaveProjectClick"
          @report="onReportProjectClick"
        />
      </div>
      <pagination
        v-if="page?.totalPages > 1"
        :pager="page"
        @change="paginate"
      />
    </div>
    <div
      v-else
      class="projects-list__content"
    >
      <div class="projects-list__empty">{{ emptyText }}</div>
    </div>
    <gf-custom-modal-window
      ref="leaveProjectModal"
      class="text-wrap"
      gf-modal-data-name="projectLeave"
      :gf-modal-header="$t('projects.leave.title')"
      gf-modal-wrapper-width="500px"
      @close="resetProjectLeaveErrors"
    >
      <template #body>
        <project-leave-form
          :alias="selectedProject?.ownerAlias"
          :project-alias="selectedProject?.alias"
          @submit="leaveProject"
        />
      </template>
    </gf-custom-modal-window>
    <gf-custom-modal-window
      ref="reportProjectModal"
      gf-modal-data-name="projectComplaint"
      :gf-modal-header="$t('projects.report.title')"
      gf-modal-wrapper-width="500px"
      @close="resetProjectReportErrors"
    >
      <template #body>
        <report-form
          report-type="project"
          :recipient-name="selectedProject?.alias"
          :is-async="true"
          @submit="reportProject"
        />
      </template>
    </gf-custom-modal-window>
  </section>
</template>

<script>
import GfProjectCard from '@shared/shared-components/shared/GfProjectCard';
import Pagination from '@shared/shared-components/shared/pagination';
import ProjectsService from '@shared/modules/projects/projects.service';
import ReportForm from '@shared/shared-components/report-form';
import PacmanLoader from '@shared/shared-components/shared/packman-loader';
import GfCustomModalWindow from '@/shared-modules/shared-components/shared/GfCustomModalWindow';
import ProjectLeaveForm from '@/shared-modules/modules/project/components/project-leave-form';
import { FORM_NAME_ENUMS } from '@/shared-modules/variables/form-name.enums';
import ProjectsMessages from '@/shared-modules/modules/projects/projects.messages';

export default {
  name: 'ProjectsList',
  components: {
    GfProjectCard,
    GfCustomModalWindow,
    ProjectLeaveForm,
    ReportForm,
    Pagination,
    PacmanLoader
  },
  props: {
    viewType: {
      type: String,
      default: null
    },
    projectsList: {
      type: Array,
      default: null
    },
    page: {
      type: Object,
      default: null
    },
    showYouOwnerLabel: {
      type: Boolean,
      default: true
    },
    emptyText: {
      type: String,
      default() {
        return this.$t('projects.none');
      }
    }
  },
  data() {
    return {
      selectedProject: null
    };
  },
  computed: {
    isUserAuthtorized() {
      return this.$store.getters.getIsUserAuthtorized;
    },
    defaultEmptyText() {
      return this.$t('projects.none');
    }
  },
  methods: {
    onLeaveProjectClick(data) {
      this.selectedProject = data;
      this.openLeaveProjectModal();
    },
    onReportProjectClick(data) {
      this.selectedProject = data;
      this.openReportProjectModal();
    },
    openLeaveProjectModal() {
      this.$refs.leaveProjectModal.openModalWindow();
    },
    closeLeaveProjectModal() {
      this.$refs.leaveProjectModal.closeModalWindow();
      this.resetProjectLeaveErrors();
    },
    openReportProjectModal() {
      this.$refs.reportProjectModal.openModalWindow();
    },
    closeReportProjectModal() {
      this.$refs.reportProjectModal.closeModalWindow();
      this.resetProjectReportErrors();
    },
    starProject(data) {
      if (this.isUserAuthtorized) {
        ProjectsService.starProject(data?.ownerAlias, data?.alias, {
          starred: data?.starred
        });
      } else {
        window.location = '/auth/login';
      }
    },
    leaveProject(data) {
      ProjectsService.leaveProject(this.selectedProject?.ownerAlias, this.selectedProject?.alias, {
        projectUuid: this.selectedProject?.uuid,
        controlString: data?.controlString
      }).then(() => {
        this.$emit('update');
        this.closeLeaveProjectModal();
      });
    },
    reportProject(data) {
      ProjectsService.reportProject(
        this.selectedProject?.ownerAlias,
        this.selectedProject?.alias,
        data
      ).then(() => {
        this.closeReportProjectModal();
      });
    },
    resetProjectLeaveErrors() {
      this.selectedProject = null;
      this.$store.dispatch('setFormErrors', {
        formName: FORM_NAME_ENUMS.LEAVE_PROJECT,
        errorResponse: null
      });
    },
    resetProjectReportErrors() {
      this.selectedProject = null;
      this.$store.dispatch('setFormErrors', {
        formName: FORM_NAME_ENUMS.REPORT_PROJECT,
        errorResponse: null
      });
    },
    paginate(page) {
      this.$router.push({ query: { ...this.$route.query, page: page - 1 } }).catch(() => {});
    }
  },
  i18n: {
    messages: ProjectsMessages
  }
};
</script>

<style lang="scss">
$this: '.projects-list';

#{$this} {
  #{$this}__content {
    min-height: 130px;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 20px;

    #{$this}__list {
      display: grid;
      grid-template-columns: repeat(1fr, 1fr);
      gap: 16px;

      &.--columns {
        grid-template-columns: repeat(1fr, 1fr);

        @media screen and (min-width: 768px) {
          grid-template-columns: repeat(2, 1fr);
        }
      }
    }

    #{$this}__empty {
      padding: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: var(--light);
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius);
    }
  }
}
</style>
